import { configureStore } from '@reduxjs/toolkit';
import authReducer from './authSlice';
import dashboardReducer from './dashboardSlice';
import personalVerificationReducer from './personalVerificationSlice';
import legalPersonReducer from './legalPersonSlice';
import usersSlice from './usersSlice';
import entryFormSlice, { initialState as entryFormInitialState } from './entryFormSlice';
import customerLegalSlice from './customerLegalSlice';

// The entry form is a multi-step flow whose data lives only in Redux. Because the
// applicant's information is submitted to the backend at the very end (after the
// cross-device eKYC step), the state must survive a tab reload/discard that some
// browsers perform on backgrounded tabs (e.g. Edge "sleeping tabs", iOS tab
// discarding). We persist it to sessionStorage so a reload does not wipe the
// applicant's input. sessionStorage (not localStorage) is used so the PII is
// cleared when the tab/session ends and does not leak across sessions.
const ENTRY_FORM_STORAGE_KEY = 'vmt4td:entryForm';

const loadEntryFormState = () => {
  try {
    const raw = sessionStorage.getItem(ENTRY_FORM_STORAGE_KEY);
    if (!raw) return undefined;
    return { entryForm: { ...entryFormInitialState, ...JSON.parse(raw) } };
  } catch {
    return undefined;
  }
};

export const store = configureStore({
  reducer: {
    auth: authReducer,
    dashboard: dashboardReducer,
    personalVerification: personalVerificationReducer,
    legalPerson: legalPersonReducer,
    users: usersSlice,
    entryForm: entryFormSlice,
    customerLegal: customerLegalSlice,
  },
  preloadedState: loadEntryFormState(),
});

store.subscribe(() => {
  try {
    sessionStorage.setItem(
      ENTRY_FORM_STORAGE_KEY,
      JSON.stringify(store.getState().entryForm)
    );
  } catch {
    // Ignore write failures (e.g. storage disabled or quota exceeded).
  }
});

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
